// SPDX-License-Identifier: AGPL-3.0-or-later

import {initializeStore} from '@app/features/platform/utils/StoreInitialization';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {WhatsNewStateSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/preferences_pb';
import {makeAutoObservable} from 'mobx';

export interface WhatsNewBlockingSurfaces {
	fullscreen: boolean;
	usernameMode: 'none' | 'prompt' | 'required';
	usernamePromptOpen: boolean;
	interestDue: boolean;
}

class WhatsNew {
	lastDismissedEntryId: string | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		initializeStore(this, () => this.initPersistence());
	}

	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'whatsNew',
			schema: WhatsNewStateSchema,
			persist: ['lastDismissedEntryId'],
			toMessage: (s) => ({
				lastDismissedEntryId: s.lastDismissedEntryId ?? undefined,
			}),
			applyMessage: (s, m) => {
				s.lastDismissedEntryId = m.lastDismissedEntryId ?? null;
			},
		});
	}

	shouldShow(entryId: string, entryDate: Date, userCreatedAt: Date): boolean {
		if (this.lastDismissedEntryId === entryId) return false;
		if (entryDate <= userCreatedAt) return false;
		return true;
	}

	shouldAutoOpen(
		entry: {id: string; date: Date} | undefined,
		userCreatedAt: Date,
		surfaces: WhatsNewBlockingSurfaces,
	): boolean {
		if (!entry) return false;
		if (surfaces.fullscreen || surfaces.interestDue) return false;
		if (surfaces.usernameMode === 'required' || (surfaces.usernameMode === 'prompt' && surfaces.usernamePromptOpen)) {
			return false;
		}
		return this.shouldShow(entry.id, entry.date, userCreatedAt);
	}

	dismiss(entryId: string): void {
		this.lastDismissedEntryId = entryId;
	}
}

export default new WhatsNew();
